import { useQuery } from '@tanstack/react-query';
import { UnifiedPortfolioService, UnifiedPortfolioItem } from '@/services/supabase/unifiedPortfolio';

export interface UseUnifiedPortfolioQueryOptions {
  profileId?: string;
  includeDrafts?: boolean;
  enabled?: boolean;
}

export function useUnifiedPortfolioQuery({
  profileId,
  includeDrafts = false,
  enabled = true
}: UseUnifiedPortfolioQueryOptions) {
  const result = useQuery({
    queryKey: ['unified-portfolio', profileId, includeDrafts],
    queryFn: () => UnifiedPortfolioService.getUnifiedPortfolio(profileId!, includeDrafts),
    enabled: enabled && !!profileId,
    staleTime: 0, // Allow data to be considered stale immediately after invalidation
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch when reconnecting
  });


  return result;
}

export function useUnifiedShotsQuery({
  profileId,
  includeDrafts = false,
  enabled = true
}: UseUnifiedPortfolioQueryOptions) {
  return useQuery({
    queryKey: ['unified-shots', profileId, includeDrafts],
    queryFn: () => UnifiedPortfolioService.getShots(profileId!, includeDrafts),
    enabled: enabled && !!profileId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

export function useUnifiedCaseStudiesQuery({
  profileId,
  includeDrafts = false,
  enabled = true
}: UseUnifiedPortfolioQueryOptions) {
  return useQuery({
    queryKey: ['unified-case-studies', profileId, includeDrafts],
    queryFn: () => UnifiedPortfolioService.getCaseStudies(profileId!, includeDrafts),
    enabled: enabled && !!profileId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

// Helper hook to get items by type
export function useUnifiedPortfolioByType(
  profileId?: string,
  includeDrafts: boolean = false
) {
  const { data: allItems, isLoading, error, refetch } = useUnifiedPortfolioQuery({
    profileId,
    includeDrafts,
  });

  const shots = allItems?.filter(item => item.item_type === 'shot') || [];
  const caseStudies = allItems?.filter(item => item.item_type === 'case_study') || [];

  return {
    allItems: allItems || [],
    shots,
    caseStudies,
    isLoading,
    error,
    refetch,
  };
}
